import { useState } from 'react';
import { Button, Group, Select, Stack, Table, Text, Title } from '@mantine/core';
import { Link, useNavigate } from 'react-router';
import { TEHLIKE_SINIFI_ETIKET } from '../i18n/tr';
import {
  BOS_KURUL_SUZGECI,
  etkinKurulSuzgeciSayisi,
  useKurulSecenekleri,
  useKurullar,
} from '../api/queries';
import type { KurulListeSuzgeci } from '../api/queries';
import { BosListeNotu, QueryStatus } from './parts';
import { Rozet, Sayfalama, SuzgecGostergesi } from '../sozlesme';
import { tarihGoster, tamSayiGoster } from '../lib/format';

/**
 * İSG KURULU — LİSTE (Kapsam §28; sayfa sözleşmesi P4 · E1).
 *
 * ⚠️ **LİSTE İŞYERİ EKSENLİDİR (C1):** bahçedeki her aktif işyeri bir satır —
 * kurulu olan · zorunlu ama kurulsuz (kırmızı) · zorunlu değil. Zorunluluk
 * SUNUCUDA türetilir; ekran *"50'den büyük mü"* diye dallanmaz.
 * ⚠️ Süzgeç ve sayfalama SUNUCUDA (tanınmayan değer 400). Kurul kurma ayrı
 * sayfada (P1), tetikleyici üstte (E1).
 */
export function KurulScreen(): React.JSX.Element {
  const [suzgec, setSuzgec] = useState<KurulListeSuzgeci>(BOS_KURUL_SUZGECI);
  const liste = useKurullar(suzgec);
  const secenekler = useKurulSecenekleri();
  const navigate = useNavigate();
  const degistir = (yama: Partial<KurulListeSuzgeci>): void =>
    setSuzgec((s) => ({ ...s, ...yama, sayfa: 1 }));

  return (
    <Stack data-testid="kurul-ekrani">
      <Group justify="space-between">
        <Title order={3}>İSG kurulu</Title>
        <Button data-testid="kurul-kur" component={Link} to="/isg-kurulu/yeni">
          Kurul kur
        </Button>
      </Group>
      <Group>
        <Select
          label="Durum"
          data-testid="kurul-durum-suzgeci"
          placeholder="Tümü"
          clearable
          value={suzgec.durum ?? null}
          onChange={(v) => degistir({ durum: (v as KurulListeSuzgeci['durum']) ?? undefined })}
          data={[
            { value: 'kurullu', label: 'Kurulu olan' },
            { value: 'zorunlu', label: 'Zorunlu ama kurulsuz' },
            { value: 'kurulsuz', label: 'Kurulsuz' },
          ]}
        />
        <Select
          label="İşyeri"
          data-testid="kurul-isyeri-suzgeci"
          placeholder="Tümü"
          clearable
          searchable
          value={suzgec.workplaceId ?? null}
          onChange={(v) => degistir({ workplaceId: v ?? undefined })}
          data={(secenekler.data?.isyerleri ?? []).map((w) => ({
            value: w.id,
            label: `${w.companyAd} — ${w.ad}`,
          }))}
        />
        <SuzgecGostergesi
          etkinSayisi={etkinKurulSuzgeciSayisi(suzgec)}
          onTemizle={() => setSuzgec(BOS_KURUL_SUZGECI)}
        />
      </Group>
      <QueryStatus queries={[liste]} />
      {liste.isSuccess &&
        (liste.data.satirlar.length === 0 ? (
          <BosListeNotu mesaj="Süzgece uyan işyeri yok." />
        ) : (
          <>
            <Table striped highlightOnHover data-testid="kurul-tablosu">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>İşyeri</Table.Th>
                  <Table.Th>Sınıf</Table.Th>
                  <Table.Th>Çalışan</Table.Th>
                  <Table.Th>Zorunluluk</Table.Th>
                  <Table.Th>Kurul</Table.Th>
                  <Table.Th>Üye</Table.Th>
                  <Table.Th>Son olağan</Table.Th>
                  <Table.Th>Sonraki vade</Table.Th>
                  <Table.Th>Açık karar</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {liste.data.satirlar.map((s) => (
                  <Table.Tr
                    key={s.workplaceId}
                    data-testid={`kurul-satiri-${s.workplaceId}`}
                    style={s.id ? { cursor: 'pointer' } : undefined}
                    onClick={() => s.id && navigate(`/isg-kurulu/${s.id}`)}
                  >
                    <Table.Td>
                      {s.id ? (
                        <Link to={`/isg-kurulu/${s.id}`}>{s.workplaceAd}</Link>
                      ) : (
                        s.workplaceAd
                      )}
                      <Text size="xs" c="dimmed">
                        {s.companyAd}
                      </Text>
                    </Table.Td>
                    <Table.Td>{TEHLIKE_SINIFI_ETIKET[s.tehlikeSinifi]}</Table.Td>
                    <Table.Td>{tamSayiGoster(s.calisanSayisi)}</Table.Td>
                    <Table.Td>
                      <Rozet
                        anlam={s.zorunlu ? (s.id ? 'olumlu' : 'engel') : 'bilgi'}
                        testId={`kurul-zorunlu-${s.workplaceId}`}
                      >
                        {s.zorunlu ? 'Zorunlu' : 'Zorunlu değil'}
                      </Rozet>
                    </Table.Td>
                    <Table.Td>
                      {s.id ? (
                        <Rozet anlam="olumlu" testId={`kurul-var-${s.workplaceId}`}>
                          Kurulu · {s.siklikAy} ayda bir
                        </Rozet>
                      ) : (
                        <Rozet
                          anlam={s.zorunlu ? 'engel' : 'bilgi'}
                          testId={`kurul-yok-${s.workplaceId}`}
                        >
                          Kurul yok
                        </Rozet>
                      )}
                    </Table.Td>
                    <Table.Td>
                      {s.id ? (
                        <Text size="sm">
                          {s.uyeSayisi}
                          {s.eksikUyeSayisi > 0 ? ` (${s.eksikUyeSayisi} eksik)` : ''}
                        </Text>
                      ) : (
                        '—'
                      )}
                    </Table.Td>
                    <Table.Td>
                      {s.sonOlaganToplantiTarihi ? tarihGoster(s.sonOlaganToplantiTarihi) : '—'}
                    </Table.Td>
                    <Table.Td>
                      {s.sonrakiVade ? (
                        <Rozet
                          anlam={s.gecikmeGunu > 0 ? 'gecikmis' : 'bilgi'}
                          testId={`kurul-vade-${s.workplaceId}`}
                        >
                          {tarihGoster(s.sonrakiVade)}
                          {s.gecikmeGunu > 0 ? ` (${s.gecikmeGunu} gün gecikmiş)` : ''}
                        </Rozet>
                      ) : (
                        '—'
                      )}
                    </Table.Td>
                    <Table.Td>{s.id ? tamSayiGoster(s.acikKararSayisi) : '—'}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            <Sayfalama
              sayfa={liste.data.sayfa}
              sayfaBoyutu={liste.data.sayfaBoyutu}
              toplam={liste.data.toplam}
              onDegis={(sayfa) => setSuzgec((s) => ({ ...s, sayfa }))}
              testId="kurul-sayfalama"
            />
          </>
        ))}
    </Stack>
  );
}
